import withParams from "@src/components/widgets/HOC/withParams";
import React from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetHeaderParams } from "../interface";
import LandingHeader from "design-system/src/Components/WidgetPart/LandingHeader/LandingHeader";
import useCtaAction, {
  ECtaAction,
} from "@src/components/widgets/hooks/useCtaAction";
import usePageLang from "@src/hooks/usePageLang";
import { AvailableLanguage } from "design-system/src/Interface/logoLang";
import { usePushAppsModal } from "@src/lib/WidgetPushApp/WidgetPushApp";
import { LandingStandardDualVideoBackground } from "design-system/src/Components/WidgetPart/LandingStandardMedia";
import { useLandingStandardDualMedia } from "../hooks/useLandingStandardDualMedia";
import { renderLandingStandardSushiShopVideo } from "../renderLandingStandardSushiShopVideo";

const LandingStandardHeader: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetHeaderParams>((props) => {
    const { p, params } = props;
    const logoId = p("logo");
    const cta_action = p("cta_action");
    const productId = p("product");
    const { generateClick } = useCtaAction(productId || "");
    const logoSource = p("logo_source") || "image";
    const attachPushApps: boolean = p("attach_to_push_apps") === "1";
    const hideCta = p("hide_cta") === "1";
    const { openModal, canShowPushApps } = usePushAppsModal();
    const lang = usePageLang();

    const rawParams = params as Record<string, unknown>;

    const headerMedia = useLandingStandardDualMedia({
      rawParams,
      prefix: "header_background",
      legacy: {
        desktopImageKey: "background_media_desktop",
        mobileImageKey: "background_media_mobile",
      },
      heroTile: {
        prefix: "header_product_visual",
        legacy: {
          desktopImageKey: "product_image",
          mobileImageKey: "product_image",
        },
        aspectRatio: 1,
        desktopImageWidth: 904,
        fallbackImageId: p("product_image") || "-1",
      },
    });

    const link = p("link");
    // URL réelle du CTA : uniquement pour l'action "open_link".
    // Permet de rendre un <a href> (SEO) ; undefined ailleurs → fallback <button>.
    const ctaUrl =
      cta_action === ECtaAction.OpenLink && link && link !== "#"
        ? link
        : undefined;

    const onClick = generateClick(
      cta_action && cta_action === ECtaAction.OpenLink
        ? {
            type: cta_action,
            link: link || "#",
          }
        : {
            type: cta_action || ECtaAction.ProductCard,
          }
    );

    const handleClick = (e?: React.MouseEvent<HTMLElement>) => {
      // Un href est rendu pour le SEO, mais la navigation reste gérée en JS
      // (routing SPA / modal push-apps) : on empêche le comportement natif du
      // lien pour éviter une double navigation.
      if (ctaUrl) e?.preventDefault();
      if (attachPushApps && canShowPushApps()) {
        openModal();
      } else {
        onClick();
      }
    };

    return (
      <LandingHeader
        lang={lang as AvailableLanguage}
        title={p("title") || ""}
        subtitle={p("subtitle")}
        ctaLabel={p("cta_label") || ""}
        description={p("description")}
        renderHeroVisual={headerMedia.renderHeroVisual}
        logoId={logoId}
        logoSource={logoSource}
        svgLogo={p("svg_logo")}
        svgLogoFile={p("svg_logo_file")}
        onClick={handleClick}
        ctaUrl={ctaUrl}
        backgroundImage={headerMedia.backgroundImageIds}
        backgroundBehindContainer={
          <LandingStandardDualVideoBackground
            dual={headerMedia.dual}
            renderVideo={renderLandingStandardSushiShopVideo}
          />
        }
        imageLeft={p("image_position") === "image-left"}
        hideCta={hideCta}
      />
    );
  });

export default LandingStandardHeader;
